import {
  CoverageProductEnum,
  DeliveryFilterType,
  DeliverySortType,
} from "@redotech/redo-ris-enums-common/ris-enums";
import * as zExt from "@redotech/rpc/ext";
import { isDef } from "@redotech/util/checks";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { specialBlockCheckoutCheckoutExperience } from "../checkouts/block-checkout";
import { SPECIAL_EXISTING_SHIPPING_METHODS_CHECKOUT_EXPERIENCE } from "../checkouts/checkout-ab-test-utils";
import { CheckoutParcelTreeSchema } from "../checkouts/parcel/parcel-tree";
import { subscriptionPreOrderDeliveryProfileSettingsSchema } from "../checkouts/subscription-pre-order-delivery-profile";
import { ShippingRateTableArraySchema } from "./shipping-rate-table";

export const TEST_CHECKOUT_OUTBOUND_RATES_NAME = "REDO_CHECKOUT_TEST";
export const TEST_CHECKOUT_OUTBOUND_RATES_TREATMENT_ID_REGEX = new RegExp(
  `${TEST_CHECKOUT_OUTBOUND_RATES_NAME}-(\\S+)`,
);

export const PREVIEW_CHECKOUT_OUTBOUND_RATES_NAME = "RCN";
export const PREVIEW_CHECKOUT_OUTBOUND_RATES_NODE_ID_REGEX = new RegExp(
  `${PREVIEW_CHECKOUT_OUTBOUND_RATES_NAME}-(\\S+)`,
);

export enum ShippingDiscountType {
  PERCENTAGE = "percentage",
  AMOUNT = "amount",
}

export const maxPriceValue = 1_000_000_000;
export const maxWeightValue = 1_000_000_000;

const DeliveryFilterTypeEnum = createEnum(DeliveryFilterType, {
  enumName: "DeliveryFilterType",
});

const DeliverySortTypeEnum = createEnum(DeliverySortType, {
  enumName: "DeliverySortType",
});

const FilterSort = z.object({
  filterType: DeliveryFilterTypeEnum.optional(),
  sortType: DeliverySortTypeEnum.optional(),
});

const CheckoutExperienceZodSchema_ = z.object({
  _id: zExt.objectId(),
  name: z.string(),
  rateTables: ShippingRateTableArraySchema,
  deleted: z.date().nullish(),
  checkoutWidgetIds: z.array(zExt.objectId()).optional(),
  checkoutParcelTree: CheckoutParcelTreeSchema.nullish(),
  activeMercuryModelId: zExt.objectId().nullish(),
  upsellPageId: zExt.objectId().optional(),
  customShippingMethodSortOrder: z.array(zExt.objectId()).optional(),
  useConstraintsV2: z.boolean().nullish(),
  fulfillmentDelayDaysOverride: z.number().nullish(),
  addLandedCosts: z.boolean().optional(),
});
export const CheckoutExperienceZodSchema: z.ZodType<CheckoutExperience> =
  CheckoutExperienceZodSchema_;
export interface CheckoutExperience extends z.infer<
  typeof CheckoutExperienceZodSchema_
> {}

export const CheckoutExperienceZodArraySchema: z.ZodType<CheckoutExperience[]> =
  z.array(CheckoutExperienceZodSchema_);

const ShippingCoverageZodSchema_ = z.object({
  coverage: createEnum(CoverageProductEnum, {
    enumName: "CoverageProductEnum",
  }).nullish(), //I don't think coverage product should live here, but I need this out quickly
  enabled: z.boolean(),
  checkoutOutboundRatesEnabled: z.boolean().optional(),
  demoCheckoutOutboundRatesEnabled: z.boolean().optional(),
  requestShippingDeliveryPermissions: z.boolean().optional(),
  checkoutExperiences: CheckoutExperienceZodArraySchema.optional(),
  rateTables: ShippingRateTableArraySchema,
  subscriptionPreOrderDeliveryProfile:
    subscriptionPreOrderDeliveryProfileSettingsSchema.optional(),
  deliverySortType: DeliverySortTypeEnum.optional(),
  deliveryFilterType: DeliveryFilterTypeEnum.optional(),
  optIn: FilterSort.optional(),
  optOut: FilterSort.optional(),
});
export const ShippingCoverageZodSchema: z.ZodType<ShippingCoverage> =
  ShippingCoverageZodSchema_;
export interface ShippingCoverage extends z.infer<
  typeof ShippingCoverageZodSchema_
> {}
export interface ShippingCoverageInterface extends z.ZodType<ShippingCoverage> {}

/**
 * WARNING: If passing includeSpecialExistingShippingMethodsCheckoutExperience as
 * true, ensure that it's properly filtered out if it's not a checkout experience
 * that's present and active in the checkout tree.
 */
export function safeParseCheckoutExperienceArray({
  checkoutExperiences,
  includeSpecialBlockCheckoutCheckoutExperience,
  includeSpecialExistingShippingMethodsCheckoutExperience = false,
}: {
  checkoutExperiences: unknown[];
  includeSpecialBlockCheckoutCheckoutExperience: boolean;
  includeSpecialExistingShippingMethodsCheckoutExperience?: boolean | undefined;
}): CheckoutExperience[] {
  const parsedCheckoutExperiences = checkoutExperiences
    .map((checkoutExperience) => {
      const result = CheckoutExperienceZodSchema.safeParse(checkoutExperience);
      if (!result.success) {
        return undefined;
      }
      return result.data;
    })
    .filter(isDef);

  if (includeSpecialExistingShippingMethodsCheckoutExperience) {
    parsedCheckoutExperiences.push(
      SPECIAL_EXISTING_SHIPPING_METHODS_CHECKOUT_EXPERIENCE,
    );
  }

  if (includeSpecialBlockCheckoutCheckoutExperience) {
    parsedCheckoutExperiences.push(specialBlockCheckoutCheckoutExperience);
  }

  return parsedCheckoutExperiences;
}
